import * as React from 'react';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RefertusLogo } from '@/components/ui/RefertusLogo';
import { 
  Check, 
  X,
  ArrowRight,
  Stethoscope,
  Users,
  Building2,
  Sparkles,
  Shield,
  Crown,
  Menu,
  Hospital,
  Home,
  Calendar,
  Zap
} from 'lucide-react';
import { useNavigate, useSearchParams } from '@/lib/router-compat';
import { Badge } from '@/components/ui/badge';
import { PlanFinder } from '@/components/pricing/PlanFinder';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DOCTOR_PLANS,
  STUDIO_PLANS,
  CLINIC_PLANS,
  REPORT_PACKAGES,
  formatPlanPrice,
  type Plan,
} from '@/lib/plans';
import { SalesContactForm } from '@/components/pricing/SalesContactForm';
import { IncrementalDoctorPlans, StructurePlansV3, EnterpriseCardV3 } from '@/components/pricing/IncrementalDoctorPlans';
import { useLanguage } from '@/contexts/LanguageContext';
import { translateFeature } from '@/lib/i18n/translations';
import type { TranslationKeys } from '@/lib/i18n/translations';
import { useAuth } from '@/hooks/useAuth';
import { useLandingVisitTracker } from '@/hooks/useLandingVisitTracker';
import { PublicFooter } from '@/components/PublicFooter';

// Helper function to map plans for display
const mapDoctorPlans = (plans: typeof DOCTOR_PLANS) => {
  return plans.map(plan => ({
    id: plan.id,
    name: plan.name,
    description: plan.description,
    price: plan.price,
    period: plan.period,
    icon: plan.icon,
    popular: plan.popular,
    features: plan.features,
    cta: plan.ctaText,
    ctaVariant: plan.ctaVariant,
    disabled: false,
  }));
};

// Helper for structure plans
const mapStructurePlans = <T extends { id: string; popular?: boolean }>(plans: T[]) => {
  return plans.map(plan => ({
    ...plan,
    popular: plan.popular,
    disabled: false,
  }));
};

function buildPlanCategories(t: TranslationKeys) {
  return {
    indipendenti: {
      title: t.pricingIndependentDoctors,
      subtitle: t.pricingIndependentSubtitle,
      icon: Stethoscope,
      badge: t.pricingOneDoctor,
      badgeColor: 'bg-muted text-muted-foreground',
      cardBorderColor: 'border-primary',
      cardShadow: 'shadow-xs',
      iconBg: 'bg-primary text-primary-foreground',
      iconBgLight: 'bg-muted text-muted-foreground',
      plans: mapDoctorPlans(DOCTOR_PLANS.filter(p => p.id !== 'test' && !p.legacy)),
    },
    studi: {
      title: t.pricingMedicalStudios,
      subtitle: t.pricingStudiosSubtitle,
      icon: Home,
      badge: t.pricingTwoFiveDoctors,
      badgeColor: 'bg-muted text-muted-foreground',
      cardBorderColor: 'border-border',
      cardShadow: 'shadow-xs',
      iconBg: 'bg-primary text-primary-foreground',
      iconBgLight: 'bg-muted text-muted-foreground',
      plans: mapStructurePlans(STUDIO_PLANS.filter(p => !p.legacy).map(plan => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        price: plan.price,
        period: plan.period,
        doctors: `${t.pricingUpTo} ${plan.maxDoctors} ${t.pricingDoctors}`,
        icon: plan.icon,
        popular: plan.popular,
        features: plan.features,
        cta: plan.ctaText,
        ctaVariant: plan.ctaVariant,
      }))),
    },
    poliambulatori: {
      title: t.pricingPolyclinics,
      subtitle: t.pricingPolyclinicsSubtitle,
      icon: Building2,
      badge: t.pricingSixFifteenDoctors,
      badgeColor: 'bg-muted text-muted-foreground',
      cardBorderColor: 'border-border',
      cardShadow: 'shadow-xs',
      iconBg: 'bg-primary text-primary-foreground',
      iconBgLight: 'bg-muted text-muted-foreground',
      plans: mapStructurePlans(CLINIC_PLANS.filter(p => p.id !== 'enterprise' && !p.legacy).map(plan => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        price: plan.price,
        period: plan.period,
        doctors: plan.maxDoctors ? `${t.pricingUpTo} ${plan.maxDoctors} ${t.pricingDoctors}` : t.pricingUnlimitedDoctors,
        icon: plan.icon,
        popular: plan.popular,
        features: plan.features,
        cta: plan.ctaText,
        ctaVariant: plan.ctaVariant,
      }))),
    },
    cliniche: {
      title: t.pricingClinicsHospitals,
      subtitle: t.pricingClinicsSubtitle,
      icon: Hospital,
      badge: t.pricingSixteenPlusDoctors,
      badgeColor: 'bg-muted text-muted-foreground',
      cardBorderColor: 'border-border',
      cardShadow: 'shadow-xs',
      iconBg: 'bg-primary text-primary-foreground',
      iconBgLight: 'bg-muted text-muted-foreground',
      plans: mapStructurePlans(CLINIC_PLANS.filter(p => p.id === 'enterprise').map(plan => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        price: plan.price,
        period: plan.period,
        doctors: t.pricingUnlimitedDoctors,
        icon: plan.icon,
        popular: true,
        features: plan.features,
        cta: plan.ctaText,
        ctaVariant: plan.ctaVariant,
      }))),
    },
  };
}

// Componente per renderizzare una categoria di piani
interface PlanCategoryProps {
  category: ReturnType<typeof buildPlanCategories>['indipendenti'];
  navigate: ReturnType<typeof useNavigate>;
  bgClass?: string;
  isUpgradeMode?: boolean;
  t: TranslationKeys;
  language: 'it' | 'en';
  isAuthenticated?: boolean;
}

function PlanCategory({ category, navigate, bgClass = '', isUpgradeMode = false, t, language, isAuthenticated = false }: PlanCategoryProps) {
  const CategoryIcon = category.icon;
  const visiblePlans = category.plans;
  
  return (
    <section className={`py-10 sm:py-16 px-4 ${bgClass}`}>
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col items-center text-center mb-8 sm:mb-10">
          <div className={`inline-flex items-center gap-2 ${category.badgeColor} px-3 sm:px-4 py-1.5 rounded-xs text-[11px] uppercase tracking-wider font-semibold mb-4`}>
            <CategoryIcon className="h-3.5 w-3.5" />
            {category.badge}
          </div>
          <h2
            className="text-2xl sm:text-3xl font-semibold text-center text-foreground"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {category.title}
          </h2>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base text-center max-w-2xl">{category.subtitle}</p>
        </div>

        <div className={`grid gap-4 sm:gap-6 ${
          visiblePlans.length === 1 
            ? 'max-w-2xl mx-auto' 
            : visiblePlans.length === 2 
              ? 'grid-cols-1 sm:grid-cols-2 max-w-4xl mx-auto' 
              : visiblePlans.length <= 3
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto'
                : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto'
        }`}>
          {visiblePlans.map((plan) => {
            const PlanIcon = plan.icon;
            const isDisabled = 'disabled' in plan && plan.disabled;
            const isHighlighted = !!plan.popular && !isDisabled;
            

            return (
              <Card 
                key={plan.name} 
                className={`p-5 sm:p-7 relative flex flex-col transition-colors bg-card ${
                  isHighlighted ? 'border-2 border-primary' : 'border border-border'
                } ${
                  isDisabled ? 'opacity-50 grayscale pointer-events-none' : ''
                }`}
              >
                {isHighlighted && (
                  <div className="absolute -top-2.5 left-6 px-2 py-0.5 rounded-xs text-[10px] font-semibold tracking-[0.14em] uppercase bg-primary/10 text-primary whitespace-nowrap z-10">
                    {language === 'it' ? 'Tutto incluso' : 'All included'}
                  </div>
                )}
                {isDisabled && (
                  <div className="absolute inset-0 bg-muted/60 rounded-lg flex items-center justify-center z-10">
                    <Badge variant="secondary" className="text-sm">
                      {t.pricingComingSoon}
                    </Badge>
                  </div>
                )}

                <div className="mb-4">
                  <h3
                    className="font-semibold text-lg text-foreground"
                    style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                  >
                    {plan.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                </div>

                <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
                  {plan.price !== null ? (
                    <>
                      <span className="text-[32px] sm:text-4xl font-semibold text-foreground tabular-nums leading-none">€{plan.price}</span>
                      <span className="text-muted-foreground text-sm">{plan.period}</span>
                    </>
                  ) : (
                    <span className="text-2xl sm:text-3xl font-semibold text-foreground">{t.pricingCustomPrice}</span>
                  )}
                </div>
                {'doctors' in plan && Boolean(plan.doctors) && (
                  <p className="text-sm font-medium mb-4 mt-1 text-foreground-soft">{String(plan.doctors)}</p>
                )}
                {!('doctors' in plan) && <div className="mb-4" />}

                <div className="my-3 h-px bg-border/60" />

                <ul className="mb-5 sm:mb-6 flex-1 space-y-2 sm:space-y-2.5">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start justify-between gap-2 border-b border-border/40 pb-1.5 last:border-b-0 min-w-0">
                      <span className="text-sm text-foreground-soft min-w-0 flex-1 break-words">{translateFeature(feature.text, language)}</span>
                      <span className={`text-xs font-medium shrink-0 ${feature.included ? 'text-primary' : 'text-muted-foreground/50'}`}>
                        {feature.included ? 'Sì' : 'No'}
                      </span>
                    </li>
                  ))}
                </ul>



                {plan.cta === 'Contatta Vendite' ? (
                  <SalesContactForm 
                    trigger={
                      <Button 
                        variant={plan.ctaVariant} 
                        className="w-full"
                        disabled={isDisabled}
                      >
                        {t.pricingContactSales}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    }
                  />
                ) : (
                  <Button 
                    variant="default" 
                    className="w-full"
                    disabled={isDisabled}
                    onClick={() => navigate(isAuthenticated ? `/checkout?plan=${plan.id}` : `/auth?plan=${plan.id}`)}
                  >
                    {plan.price === 0
                      ? (language === 'it' ? 'Attiva' : 'Activate')
                      : (language === 'it' ? 'Attiva abbonamento' : 'Start subscription')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Pricing() {
  // §566 — tracking visite anonimo (destinazione plausibile campagne)
  useLandingVisitTracker('/prezzi');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  // showFullCompare rimosso: confronto piano-per-piano non più esposto (Pivot v3.0).
  // Prompt 2 (2026-07-13): selettore due-aree "Medico singolo" vs "Studio/Struttura".
  // Filtra le sezioni mostrate senza mescolarle. Default: 'doctor' (medico singolo).
  const initialAudience = (searchParams.get('audience') === 'structure' ? 'structure' : 'doctor') as 'doctor' | 'structure';
  const [audience, setAudience] = React.useState<'doctor' | 'structure'>(initialAudience);

  const handleNavClick = (hash: string) => {
    setMobileMenuOpen(false);
    navigate('/');
    setTimeout(() => {
      const element = document.querySelector(hash);
      if (element) {
        const offset = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: offset, behavior: 'smooth' });
      }
    }, 800);
  };
  
  // §158: un utente loggato è SEMPRE in upgrade mode quando visita /prezzi.
  // Questo evita "Torna alla home" → SmartLanding → apertura accidentale di un referto draft.
  // Lo stesso vale se torna da Stripe con ?checkout=cancelled.
  const isUpgradeMode =
    searchParams.get('upgrade') === '1' ||
    searchParams.get('checkout') === 'cancelled' ||
    isLoggedIn;
  const planCategories = React.useMemo(() => buildPlanCategories(t), [t]);

  const faqs = React.useMemo(() => [
    {
      question: language === 'it'
        ? 'Le funzioni di sicurezza medico-legale sono a pagamento extra?'
        : 'Are medical-legal security features a paid add-on?',
      answer: language === 'it'
        ? 'No. Impronta SHA-256, marca temporale RFC 3161, QR di verifica e audit trail sono incluse in ogni piano, senza costi aggiuntivi o upgrade necessari.'
        : 'No. SHA-256 fingerprint, RFC 3161 timestamp, verification QR and audit trail are included in every plan, with no extra cost or upgrade required.',
    },
    { question: t.pricingFaq1Q, answer: t.pricingFaq1A },
    { question: t.pricingFaq2Q, answer: t.pricingFaq2A },
    { question: t.pricingFaq3Q, answer: t.pricingFaq3A },
    { question: t.pricingFaq4Q, answer: t.pricingFaq4A },
    { question: t.pricingFaq5Q, answer: t.pricingFaq5A },
    { question: t.pricingFaq6Q, answer: t.pricingFaq6A },
    { question: t.pricingFaq7Q, answer: t.pricingFaq7A },
    { question: t.pricingFaq8Q, answer: t.pricingFaq8A },
    { question: (t as any).pricingFaq9Q, answer: (t as any).pricingFaq9A },
  ], [t, language]);

  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const timerId = setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timerId);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div onClick={() => navigate(isUpgradeMode ? '/app' : '/')} className="cursor-pointer">
            <RefertusLogo size="sm" />
          </div>
          
          {isUpgradeMode ? (
            <Button variant="ghost" size="sm" onClick={() => navigate('/app')} className="text-xs sm:text-sm">
              ← Torna alla Dashboard
            </Button>
          ) : (
            <button 
              onClick={() => navigate('/')} 
              className="text-sm font-medium text-muted-foreground hover:text-muted-foreground transition-colors"
            >
              ← Torna alla home
            </button>
          )}
        </div>
      </header>


      {/* Hero */}
      <section className="py-10 sm:py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          {/* §553 — h1 pubblico sempre presente: prima esisteva solo in isUpgradeMode
              e la versione pubblica di /prezzi risultava senza <h1> (Bing WMT). */}
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground mb-4 tracking-tight"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {isUpgradeMode
              ? t.pricingUpgradeTitle
              : language === 'it'
                ? 'Scegli la capacità che ti serve.'
                : 'Choose the capacity you need.'}
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            {isUpgradeMode
              ? t.pricingUpgradeSubtitle
              : language === 'it'
                ? 'Ogni piano include una capacità mensile. Quando il volume delle tue visite cresce, puoi aggiungere capacità quando ne hai bisogno.'
                : 'Every plan includes a monthly capacity. When your visit volume grows, you can add capacity whenever you need it.'}
          </p>
          {/* Prompt 2 (2026-07-13): Selettore due-aree "Medico singolo" vs "Studio/Struttura".
              §544 (redesign): stile tab sobrio, non pill consumer. */}
          <div
            className="inline-flex bg-card border border-border rounded-md overflow-hidden"
            role="tablist"
            aria-label={language === 'it' ? 'Seleziona tipo di utilizzo' : 'Select usage type'}
          >
            <button
              type="button"
              role="tab"
              onClick={() => setAudience('doctor')}
              className={`inline-flex items-center gap-2 px-5 sm:px-7 py-2.5 text-sm font-medium transition-colors border-r border-border ${
                audience === 'doctor'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground-soft hover:bg-muted/60'
              }`}
              aria-pressed={audience === 'doctor'}
              aria-selected={audience === 'doctor'}
            >
              <Stethoscope className="h-4 w-4" />
              {language === 'it' ? 'Medico singolo' : 'Single doctor'}
            </button>
            <button
              type="button"
              role="tab"
              onClick={() => setAudience('structure')}
              className={`inline-flex items-center gap-2 px-5 sm:px-7 py-2.5 text-sm font-medium transition-colors ${
                audience === 'structure'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground-soft hover:bg-muted/60'
              }`}
              aria-pressed={audience === 'structure'}
              aria-selected={audience === 'structure'}
            >
              <Building2 className="h-4 w-4" />
              {language === 'it' ? 'Studio o struttura' : 'Studio or clinic'}
            </button>
          </div>
        </div>
      </section>


      {/* Claim "Tutto incluso" — Pivot v3.0 */}
      <section className="pb-2 sm:pb-6 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <p
            className="text-lg sm:text-xl font-semibold text-foreground"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {language === 'it'
              ? 'Un piano. Tutte le funzionalità. Nessuna funzione di sicurezza bloccata dietro un piano superiore.'
              : 'One plan. All features. No security function locked behind a higher tier.'}
          </p>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            {language === 'it'
              ? 'Impronta SHA-256, marca temporale RFC 3161 e audit trail completo, in ogni piano. L\'originale è il referto stampato e firmato a penna dal medico; al paziente arrivano copie di cortesia.'
              : 'SHA-256 digital fingerprint, RFC 3161 timestamp (simple electronic signature, not a digital signature), full audit trail: included from day one, in every plan. Not an upgrade — the standard.'}
          </p>
        </div>
      </section>


      {audience === 'doctor' && (
      <>
      {/* 1. Medici Indipendenti — card incrementali */}
      <section id="indipendenti" className="py-10 sm:py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col items-center text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-muted text-muted-foreground px-3 py-1.5 rounded-xs text-[11px] uppercase tracking-wider font-semibold mb-4">
              <Stethoscope className="h-3.5 w-3.5" />
              {t.pricingOneDoctor}
            </div>
            <h2
              className="text-2xl sm:text-3xl font-semibold text-center text-foreground"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            >
              {t.pricingIndependentDoctors}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base text-center max-w-2xl">
              {language === 'it'
                ? 'Prova gratuita 14 giorni, poi il piano Individuale con tutto incluso.'
                : '14-day free trial, then the Individual plan with everything included.'}
            </p>
          </div>

          <IncrementalDoctorPlans
            onSelect={(planId) => navigate(isLoggedIn ? `/checkout?plan=${planId}` : `/auth?plan=${planId}`)}
            ctaLabel={(planId) => planId === 'trial' ? (language === 'it' ? 'Inizia prova gratuita' : 'Start free trial') : (language === 'it' ? 'Scegli piano' : 'Select plan')}
          />

          {/* Confronto piano-per-piano rimosso (Pivot v3.0): esiste un solo piano attivo per segmento. */}
        </div>
      </section>
      </>
      )}




      {audience === 'structure' && (
      <>
      {/* 2. Struttura — STESSO template card di "Individuale" (PlanCardV3) */}
      <section id="studi" className="py-10 sm:py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col items-center text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-muted text-muted-foreground px-3 py-1.5 rounded-xs text-[11px] uppercase tracking-wider font-semibold mb-4">
              <Home className="h-3.5 w-3.5" />
              {t.pricingTwoFiveDoctors}
            </div>
            <h2
              className="text-2xl sm:text-3xl font-semibold text-center text-foreground"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            >
              {t.pricingMedicalStudios}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base text-center max-w-2xl">
              {language === 'it'
                ? 'Un solo piano Struttura, tutto incluso — governance, multi-medico, forense.'
                : 'One Structure plan, everything included — governance, multi-doctor, forensic.'}
            </p>
          </div>

          <StructurePlansV3
            onSelect={(planId) => navigate(isLoggedIn ? `/checkout?plan=${planId}` : `/auth?plan=${planId}`)}
            ctaLabel={(_planId) => language === 'it' ? 'Attiva Struttura' : 'Activate Structure'}
          />
        </div>
      </section>

      {/* 3. Cliniche e Ospedali (Enterprise — Contact Sales)
          STESSO template PlanCardV3, senza badge "Tutto incluso" (prezzo su misura). */}
      <section id="cliniche" className="py-10 sm:py-16 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col items-center text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-muted text-muted-foreground px-3 py-1.5 rounded-xs text-[11px] uppercase tracking-wider font-semibold mb-4">
              <Hospital className="h-3.5 w-3.5" />
              {t.pricingSixteenPlusDoctors}
            </div>
            <h2
              className="text-2xl sm:text-3xl font-semibold text-center text-foreground"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            >
              {t.pricingClinicsHospitals}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base text-center max-w-2xl">
              {language === 'it'
                ? 'Cliniche e ospedali con oltre 15 medici: volumi, SLA e onboarding su misura.'
                : 'Clinics and hospitals with more than 15 doctors: custom volumes, SLA and onboarding.'}
            </p>
          </div>

          <EnterpriseCardV3 ctaLabel={language === 'it' ? 'Contatta vendite' : 'Contact sales'} />
        </div>
      </section>

      </>
      )}

      {/* Report Packages — visibili SEMPRE, DOPO la card del piano (Medico singolo e Struttura) */}
      <section id="referti-extra" className="py-10 sm:py-16 px-4 bg-muted/20">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-4">
              {language === 'it' ? 'Capacità aggiuntiva' : 'Additional capacity'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">
              {language === 'it' ? 'Ti serve più capacità?' : 'Need more capacity?'}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base max-w-xl mx-auto">
              {language === 'it'
                ? 'Aggiungi capacità quando il volume delle visite supera quello incluso nel tuo piano. La capacità aggiuntiva non scade e resta nel tuo account fino all\'ultimo credito usato. Acquistabile dalla tua Dashboard personale.'
                : 'Add capacity when your visit volume exceeds the amount included in your plan. Additional capacity never expires and stays in your account until the last credit is used. Available from your personal Dashboard.'}
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4 max-w-4xl mx-auto">
            {REPORT_PACKAGES.map((pkg) => (
              <Card
                key={pkg.id}
                className="p-5 sm:p-6 flex flex-col items-center text-center border border-border"
              >
                <div className="text-3xl sm:text-4xl font-normal text-muted-foreground mb-1">+{pkg.reports}</div>
                <div className="text-sm text-muted-foreground mb-3">{language === 'it' ? 'crediti' : 'credits'}</div>
                <div className="text-2xl font-normal text-muted-foreground mb-1">€{pkg.price.toFixed(2).replace('.', ',')}</div>
                <div className="text-xs text-muted-foreground">€{pkg.pricePerReport.toFixed(2).replace('.', ',')}/{language === 'it' ? 'credito' : 'credit'}</div>
              </Card>
            ))}
          </div>

          <div className="max-w-2xl mx-auto mt-8 sm:mt-10 text-center">
            <p className="text-sm sm:text-base text-muted-foreground font-medium mb-2">
              {language === 'it'
                ? '📄 La capacità aggiuntiva acquistata non scade mai.'
                : '📄 Purchased additional capacity never expires.'}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              {language === 'it'
                ? 'A differenza della capacità mensile inclusa nel piano (che si resetta ogni mese), la capacità aggiuntiva acquistata a pacchetti viene sommata al tuo saldo permanente e consumata solo dopo aver esaurito quella mensile del piano. Resta nel tuo account fino al completo utilizzo, senza alcuna scadenza temporale.'
                : 'Unlike the monthly capacity included in your plan (which resets each month), additional capacity purchased in packages is added to your permanent balance and consumed only after your monthly plan capacity is exhausted. It remains in your account until fully used, with no time limit.'}
            </p>
          </div>
        </div>
      </section>




      {/* FAQ */}
      <section className="py-8 sm:py-12 md:py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-6 sm:mb-8 md:mb-12">{t.pricingFaqTitle}</h2>
          
          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="p-3 sm:p-4 md:p-6">
                <h3 className="font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">{faq.question}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm md:text-base whitespace-pre-line">{faq.answer}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">{t.pricingCtaTitle}</h2>
          <p className="text-primary-foreground/80 mb-6 sm:mb-8 text-sm sm:text-base px-2">
            {t.pricingCtaDesc}
          </p>
          <div className="flex flex-col gap-3 sm:gap-4 justify-center px-4 sm:px-0 sm:flex-row">
            <Button 
              size="lg" 
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => navigate('/auth?register=1')}
            >
              {t.pricingStartFree}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <SalesContactForm 
              trigger={
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full sm:w-auto bg-transparent border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10"
                >
                  {t.pricingContactSales}
                </Button>
              }
            />
          </div>
        </div>
      </section>

      <PublicFooter />
      </div>
    </>
  );
}
